import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  UserCircle,
  MailCheck,
  MailWarning,
  Camera,
  KeyRound,
  ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { getErrorMessage } from "../../lib/apiClient";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import {
  changePassword,
  confirmTwoFactorSetup,
  disableTwoFactor,
  fetchAvatar,
  removeAvatar,
  requestEmailVerification,
  startTwoFactorSetup,
  updateProfile,
  uploadAvatar,
} from "./authApi";

export default function ProfilePage() {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();
  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [passwords, setPasswords] = useState({
    current: "",
    next: "",
    confirm: "",
  });
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  useEffect(() => {
    if (user?.displayName) setDisplayName(user.displayName);
  }, [user?.displayName]);

  useEffect(() => {
    if (!user?.avatarUpdatedAt) return undefined;
    let url;
    fetchAvatar()
      .then((blob) => {
        url = URL.createObjectURL(blob);
        setAvatarUrl(url);
      })
      .catch(() => undefined);
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [user?.avatarUpdatedAt]);

  const saveMutation = useMutation({
    mutationFn: () => updateProfile({ displayName: displayName.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.success("Profile updated");
    },
    onError: (err) =>
      toast.error(getErrorMessage(err, "Could not update profile")),
  });

  const resendMutation = useMutation({
    mutationFn: requestEmailVerification,
    onSuccess: () => toast.success("Verification email sent"),
    onError: (err) =>
      toast.error(getErrorMessage(err, "Could not send verification email")),
  });

  const verified = Boolean(user?.emailVerifiedAt);
  const avatarMutation = useMutation({
    mutationFn: uploadAvatar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.success("Profile picture updated");
    },
    onError: (err) =>
      toast.error(getErrorMessage(err, "Could not update profile picture")),
  });
  const removeAvatarMutation = useMutation({
    mutationFn: removeAvatar,
    onSuccess: () => {
      setAvatarUrl("");
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
  const passwordMutation = useMutation({
    mutationFn: () => changePassword(passwords.current, passwords.next),
    onSuccess: () => {
      setPasswords({ current: "", next: "", confirm: "" });
      toast.success("Password changed. Sign in again on your other devices.");
    },
    onError: (err) =>
      toast.error(getErrorMessage(err, "Could not change password")),
  });
  const startTwoFactorMutation = useMutation({
    mutationFn: startTwoFactorSetup,
    onSuccess: setTwoFactorSetup,
    onError: (err) =>
      toast.error(getErrorMessage(err, "Could not start two-factor setup")),
  });
  const confirmTwoFactorMutation = useMutation({
    mutationFn: () => confirmTwoFactorSetup(twoFactorCode),
    onSuccess: (data) => {
      setRecoveryCodes(data.recoveryCodes);
      setTwoFactorSetup(null);
      queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.success("Two-factor authentication enabled");
    },
    onError: (err) =>
      toast.error(getErrorMessage(err, "Authenticator code is not valid")),
  });
  const disableTwoFactorMutation = useMutation({
    mutationFn: () => disableTwoFactor(twoFactorCode),
    onSuccess: () => {
      setTwoFactorCode("");
      queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.success("Two-factor authentication disabled");
    },
    onError: (err) =>
      toast.error(
        getErrorMessage(err, "Could not disable two-factor authentication"),
      ),
  });

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className="flex items-center gap-2 text-lg font-semibold text-slate-800">
        <UserCircle className="h-5 w-5 text-slate-500" />
        Profile
      </h1>

      <section className="space-y-4 rounded-xl border border-slate-200 bg-slate-100 p-5 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-brand-100 text-xl font-semibold text-brand-700">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Profile"
                className="h-full w-full object-cover"
              />
            ) : (
              (user?.displayName?.[0] ?? "?").toUpperCase()
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700">
              <Camera className="h-4 w-4" /> Upload picture
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="sr-only"
                onChange={(event) =>
                  event.target.files?.[0] &&
                  avatarMutation.mutate(event.target.files[0])
                }
              />
            </label>
            {user?.avatarUpdatedAt ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => removeAvatarMutation.mutate()}
              >
                Remove
              </Button>
            ) : null}
          </div>
        </div>
        <Input label="Email" value={user?.email ?? ""} disabled readOnly />
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (displayName.trim()) saveMutation.mutate();
          }}
        >
          <Input
            label="Display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <Button
            type="submit"
            loading={saveMutation.isPending}
            disabled={
              !displayName.trim() || displayName.trim() === user?.displayName
            }
          >
            Save changes
          </Button>
        </form>
      </section>

      <section className="rounded-xl border border-slate-200 bg-slate-100 p-5 shadow-sm">
        <h2 className="mb-1 flex items-center gap-2 font-semibold text-slate-800">
          <KeyRound className="h-4 w-4" /> Password
        </h2>
        <p className="mb-4 text-sm text-slate-500">
          Use a unique password you do not use elsewhere.
        </p>
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (passwords.next === passwords.confirm) passwordMutation.mutate();
            else toast.error("New passwords do not match");
          }}
        >
          <Input
            label="Current password"
            type="password"
            value={passwords.current}
            onChange={(e) =>
              setPasswords({ ...passwords, current: e.target.value })
            }
          />
          <Input
            label="New password"
            type="password"
            value={passwords.next}
            onChange={(e) =>
              setPasswords({ ...passwords, next: e.target.value })
            }
          />
          <Input
            label="Confirm new password"
            type="password"
            value={passwords.confirm}
            onChange={(e) =>
              setPasswords({ ...passwords, confirm: e.target.value })
            }
          />
          <div className="flex items-center justify-between gap-3">
            <Button type="submit" loading={passwordMutation.isPending}>
              Change password
            </Button>
            <Link
              to={`/forgot-password?email=${encodeURIComponent(user?.email ?? "")}`}
              className="text-sm text-brand-600 hover:underline"
            >
              Forgot password?
            </Link>
          </div>
        </form>
      </section>

      <section className="rounded-xl border border-slate-200 bg-slate-100 p-5 shadow-sm">
        <h2 className="flex items-center gap-2 font-semibold text-slate-800">
          <ShieldCheck className="h-4 w-4" /> Two-factor authentication
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Protect sign-in with codes from any authenticator app.
        </p>
        <div id="two-factor-settings" className="mt-4 space-y-3">
          {recoveryCodes.length ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
              <p className="font-semibold">
                Save these recovery codes now. They are shown only once.
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2 font-mono">
                {recoveryCodes.map((code) => (
                  <code key={code}>{code}</code>
                ))}
              </div>
              <Button
                className="mt-3"
                variant="secondary"
                size="sm"
                onClick={() =>
                  navigator.clipboard.writeText(recoveryCodes.join("\n"))
                }
              >
                Copy codes
              </Button>
            </div>
          ) : null}
          {twoFactorSetup ? (
            <div className="space-y-3">
              <img
                src={twoFactorSetup.qrDataUrl}
                alt="Authenticator app setup QR code"
                className="h-48 w-48 rounded-lg border bg-white p-2"
              />
              <p className="text-sm text-slate-600">
                Scan the code with any authenticator app (Google Authenticator, Microsoft Authenticator, Authy, 1Password, etc.), then enter the six-digit
                code.
              </p>
              <Input
                label="Authenticator code"
                inputMode="numeric"
                value={twoFactorCode}
                onChange={(event) =>
                  setTwoFactorCode(
                    event.target.value.replace(/\D/g, "").slice(0, 6),
                  )
                }
              />
              <Button
                loading={confirmTwoFactorMutation.isPending}
                disabled={twoFactorCode.length !== 6}
                onClick={() => confirmTwoFactorMutation.mutate()}
              >
                Confirm and enable
              </Button>
            </div>
          ) : user?.twoFactorEnabledAt ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-green-700">
                Two-factor authentication is enabled.
              </p>
              <Input
                label="Authenticator code"
                inputMode="numeric"
                value={twoFactorCode}
                onChange={(event) =>
                  setTwoFactorCode(
                    event.target.value.replace(/\D/g, "").slice(0, 6),
                  )
                }
              />
              <Button
                variant="secondary"
                loading={disableTwoFactorMutation.isPending}
                disabled={twoFactorCode.length !== 6}
                onClick={() => disableTwoFactorMutation.mutate()}
              >
                Disable two-factor authentication
              </Button>
            </div>
          ) : (
            <Button
              loading={startTwoFactorMutation.isPending}
              onClick={() => startTwoFactorMutation.mutate()}
            >
              Set up authenticator app
            </Button>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-slate-100 p-5 shadow-sm">
        <h2 className="mb-2 text-sm font-semibold text-slate-700">
          Email verification
        </h2>
        {verified ? (
          <p className="flex items-center gap-2 text-sm text-green-700">
            <MailCheck className="h-4 w-4" /> Your email is verified.
          </p>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-center gap-2 text-sm text-amber-700">
              <MailWarning className="h-4 w-4" /> Your email is not verified
              yet.
            </p>
            <Button
              variant="secondary"
              size="sm"
              loading={resendMutation.isPending}
              onClick={() => resendMutation.mutate()}
            >
              Resend verification email
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
